import { Box, Flex, Skeleton } from '@chakra-ui/react';
import type { Msg, User } from '@halo/chat-sdk-react';
import type { ReactNode } from 'react';
import { isFileMessage, isStickerMessage } from '@halo/chat-sdk';
import { FileTypeIcon, StickerTypeIcon } from '@halo/ui-chat-react';
import { useTranslation } from 'react-i18next';

export interface MessageReplyProps {
  file?: string;
  user?: User | null;
  children: ReactNode;
  loading?: boolean;
  isDeleted?: boolean;
  msg?: Msg | null;
  onClick?: () => void;
  borderColor?: string;
  bgContentColor?: string;
}

function MessageReply({
  user,
  children,
  isDeleted,
  loading,
  msg,
  onClick,
  borderColor,
  bgContentColor,
}: MessageReplyProps) {
  const isFile = useMemo(() => isFileMessage({ payload_type: msg?.payload_type as any }), [msg]);
  const isSticker = useMemo(() => isStickerMessage(msg), [msg]);
  const { t } = useTranslation();
  return (
    <Box
      className="MessageReply"
      bg={isDeleted ? 'bg.subtle' : bgContentColor}
      // position="relative"
      borderRadius="6px 12px 12px 6px"
      borderLeft="3px solid"
      borderColor={isDeleted ? 'grey.88' : borderColor}
      p="1.5"
      cursor={isDeleted ? 'default' : 'pointer'}
      onClick={() => {
        if (!isDeleted) {
          onClick?.();
        }
      }}
    >
      <Flex align="center" gap="1.5" overflow="hidden">
        {!isDeleted && isSticker && (
          <StickerTypeIcon
            sticker_id={msg?.payload?.id}
            category={msg?.payload?.stickerSetInfo?.category}
          />
        )}
        {!isDeleted && isFile && (
          <FileTypeIcon
            width={30}
            height={30}
            style={{ borderRadius: 4 }}
            extType={msg?.payload?.ext}
            src={msg?.payload?.thumbnail}
          />
        )}
        <Box overflow="hidden" flex="1 1 auto">
          {!isDeleted && (
            <Box
              fontSize="sm"
              fontWeight="semibold"
              lineHeight="normal"
              mb="2px"
              color={borderColor}
            >
              {loading ? (
                <Skeleton w="100px" display="inline-block">
                  &nbsp;
                </Skeleton>
              ) : (
                user?.original_name
              )}
              &nbsp;
            </Box>
          )}
          <Box
            fontSize="xs"
            color="fg.muted"
            fontWeight="medium"
            lineHeight={isDeleted ? '31px' : 'normal'}
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {loading ? (
              <Skeleton w="full" display="inline-block">
                &nbsp;
              </Skeleton>
            ) : isDeleted ? (
              t('chat.messageDeleted')
            ) : (
              children
            )}
            &nbsp;
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}
const MemoizedMessageReply = memo(MessageReply);
export { MemoizedMessageReply as MessageReply };
